import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Loader2, Plus } from "lucide-react";
import { BomEditor } from "@/components/boms/BomEditor";
import { blankSheet } from "@/lib/modelSheets";

const BomCard = ({ b, onOpen, showSeason }) => {
  const fabrics = (b.tech?.fabrics || []).filter((f) => f.fabric_type || f.name);
  const accs = (b.tech?.accessories || []).filter((a) => a.name);
  const noQty = [...fabrics.filter((f) => !Number(String(f.meters_per_piece ?? "").replace(",", "."))), ...accs.filter((a) => !String(a.quantity || "").trim())].length;
  return (
    <Card className="cursor-pointer space-y-2 p-4 transition-colors hover:border-primary/60" onClick={onOpen} data-testid={`bom-card-${b.id}`}>
      <div className="flex items-center gap-2">
        {b.code && <span className="rounded-md bg-primary px-2 py-0.5 font-mono-erp text-sm font-bold tracking-wider text-primary-foreground">{b.code.toUpperCase()}</span>}
        <p className="truncate font-display font-semibold">{b.name}</p>
        {showSeason && <span className="ml-auto text-xs text-muted-foreground">{b.season}</span>}
      </div>
      <p className="text-xs text-muted-foreground">{fabrics.length} {fabrics.length === 1 ? "tessuto" : "tessuti"} · {accs.length} {accs.length === 1 ? "accessorio" : "accessori"}</p>
      {noQty > 0 && <p className="flex gap-1 text-[11px] text-amber-400" data-testid={`bom-card-gaps-${b.id}`}><AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />{noQty} {noQty === 1 ? "consumo mancante" : "consumi mancanti"}</p>}
    </Card>
  );
};

export const BomsSection = ({ season, newSeason, search }) => {
  const { data: boms = [], isLoading } = useQuery({ queryKey: ["boms", season], queryFn: async () => (await api.get("/boms", { params: { season } })).data });
  const [editing, setEditing] = useState(null);
  const shown = useMemo(() => {
    const w = search.toLowerCase().split(/\s+/).filter(Boolean);
    return boms.filter((b) => w.every((x) => `${b.code} ${b.name}`.toLowerCase().includes(x)));
  }, [boms, search]);
  return (
    <div data-testid="boms-section">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">Il primo passo di ogni articolo: consumi di tessuti e accessori per capo, senza colori. I materiali esatti si trovano in pianificazione dai colori e dalle taglie che vuoi produrre.</p>
        <Button onClick={() => setEditing({ ...blankSheet(newSeason), tech: { colors: [], fabrics: [], accessories: [] } })} disabled={!newSeason} data-testid="new-bom-btn"><Plus className="mr-2 h-4 w-4" />Nuova distinta base</Button>
      </div>
      {isLoading ? <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> : shown.length === 0 ? (
        <p className="py-16 text-center text-sm text-muted-foreground" data-testid="boms-empty">{boms.length ? "Nessuna distinta corrisponde alla ricerca." : "Nessuna distinta base per questa stagione: crea la prima con \"Nuova distinta base\"."}</p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">{shown.map((b) => <BomCard key={b.id} b={b} onOpen={() => setEditing(b)} showSeason={season === "tutti"} />)}</div>
      )}
      {editing && <BomEditor bom={editing} onClose={() => setEditing(null)} onSaved={(d) => setEditing((p) => ({ ...p, ...d }))} />}
    </div>
  );
};
